import React, { useEffect, useRef, useState } from "react";
import { motion, animate } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { fetchEvents } from "../../services/firestoreData";
import { fetchDiscordMemberCount } from "../../services/publicApi";

const AnimatedCounter = ({ from = 0, to, suffix = "", duration = 2.5 }: { from?: number, to: number, suffix?: string, duration?: number }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  const nodeRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (inView && nodeRef.current) {
      const node = nodeRef.current;
      const controls = animate(from, to, {
        duration: duration,
        ease: "easeOut",
        onUpdate(value) {
          node.textContent = Math.round(value) + suffix;
        },
      });

      return () => controls.stop();
    }
  }, [inView, from, to, duration, suffix]);

  return <span ref={(el) => {
    ref(el);
    nodeRef.current = el as HTMLSpanElement;
  }} className="fw-bold d-inline-block" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#38bdf8", textShadow: "0 0 20px rgba(56, 189, 248, 0.4)", fontFamily: "'JetBrains Mono', monospace", lineHeight: "1" }} />;
};

const QuickHighlights: React.FC = () => {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const [eventCount, setEventCount] = useState<number>(0);
    const [discordCount, setDiscordCount] = useState<number>(0);

    useEffect(() => {
        // Fetch published events count
        fetchEvents({ publishedOnly: true })
            .then(events => {
                if (events && events.length > 0) {
                    setEventCount(events.length);
                }
            })
            .catch(err => {
                console.error("Failed to fetch event count for highlights:", err);
            });

        // Fetch dynamic Discord members count
        fetchDiscordMemberCount()
            .then(count => {
                if (count > 0) {
                    setDiscordCount(count);
                }
            })
            .catch(console.error);
    }, []);

  return (
    <section 
      ref={ref}
      className="position-relative w-100 py-5" 
      style={{ 
        background: "linear-gradient(90deg, #020617 0%, #0f172a 50%, #020617 100%)",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        overflow: "hidden"
      }}
    >
      {/* Background ambient glow */}
      <div 
        className="position-absolute top-50 start-50 translate-middle w-100" 
        style={{
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(14, 165, 233, 0.3), transparent)",
            boxShadow: "0 0 40px 30px rgba(14, 165, 233, 0.05)",
            zIndex: 0
        }}
      />
      
      <div className="container position-relative z-1 py-3">
        <div className="row g-4 justify-content-center align-items-center text-center">
            
            {/* Stat 1 - LinkedIn */}
            <motion.div 
               initial={{ opacity: 0, scale: 0.8 }}
               animate={inView ? { opacity: 1, scale: 1 } : {}}
               transition={{ duration: 0.6, delay: 0.1, type: "spring" }}
               className="col-6 col-md-3 d-flex flex-column align-items-center"
            >
                <AnimatedCounter to={110} suffix="+" />
                <span className="text-secondary mt-3 fw-semibold text-uppercase" style={{ letterSpacing: "2px", fontSize: "0.85rem" }}>LinkedIn Followers</span>
            </motion.div>

            {/* Stat 2 - Instagram */}
            <motion.div 
               initial={{ opacity: 0, scale: 0.8 }}
               animate={inView ? { opacity: 1, scale: 1 } : {}}
               transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
               className="col-6 col-md-3 d-flex flex-column align-items-center"
            >
                <AnimatedCounter to={227} suffix="+" />
                <span className="text-secondary mt-3 fw-semibold text-uppercase" style={{ letterSpacing: "2px", fontSize: "0.85rem" }}>Instagram Followers</span>
            </motion.div>
            
            {/* Stat 3 - Discord */}
            <motion.div 
               initial={{ opacity: 0, scale: 0.8 }}
               animate={inView ? { opacity: 1, scale: 1 } : {}}
               transition={{ duration: 0.6, delay: 0.3, type: "spring" }}
               className="col-6 col-md-3 d-flex flex-column align-items-center"
            >
                <AnimatedCounter to={discordCount || 26} suffix="+" />
                <span className="text-secondary mt-3 fw-semibold text-uppercase" style={{ letterSpacing: "2px", fontSize: "0.85rem" }}>Discord Members</span>
            </motion.div>

            {/* Stat 4 - Dynamic Events */}
            <motion.div 
               initial={{ opacity: 0, scale: 0.8 }}
               animate={inView ? { opacity: 1, scale: 1 } : {}}
               transition={{ duration: 0.6, delay: 0.4, type: "spring" }}
               className="col-6 col-md-3 d-flex flex-column align-items-center"
            >
                <AnimatedCounter to={eventCount || 25} suffix="+" />
                <span className="text-secondary mt-3 fw-semibold text-uppercase" style={{ letterSpacing: "2px", fontSize: "0.85rem" }}>Events Conducted</span>
            </motion.div>

        </div>
      </div>
    </section>
  );
};

export default QuickHighlights;
